import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { Card } from '../components/ui/Card';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { TargetIcon, GlobeIcon, UsersIcon, HandshakeIcon, AwardIcon, BuildingIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const pillars = [{
  icon: HandshakeIcon,
  title: 'Strategic Partnerships',
  description: 'Building bridges between the African Union, member states, and global partners to advance shared development priorities.'
}, {
  icon: GlobeIcon,
  title: 'Global Representation',
  description: "Representing the African Union's interests across the United Kingdom, Europe, and the wider international community."
}, {
  icon: TargetIcon,
  title: 'Project Delivery',
  description: 'Coordinating and overseeing transformative projects in health, education, trade, infrastructure, and innovation.'
}, {
  icon: AwardIcon,
  title: 'Transparent Procurement',
  description: 'Conducting fair, competitive procurement processes that uphold AU standards of integrity and accountability.'
}];
const leadership = [{
  name: 'H.E. Dr. Adaeze Okonkwo',
  role: 'Regional Director',
  bio: 'Over 25 years of experience in international development, diplomacy, and African Union strategic initiatives.',
  initials: 'AO'
}, {
  name: 'Mr. Kwame Mensah',
  role: 'Head of Procurement',
  bio: 'Procurement and contract management specialist with extensive experience in multilateral institutions.',
  initials: 'KM'
}, {
  name: 'Dr. Fatima Diallo',
  role: 'Head of Programmes',
  bio: 'Programme delivery expert leading flagship projects across health, education, and economic development sectors.',
  initials: 'FD'
}, {
  name: 'Ms. Sarah Mwangi',
  role: 'Head of Partnerships',
  bio: 'Cultivating strategic partnerships with governments, multilateral agencies, the private sector, and civil society.',
  initials: 'SM'
}];
const timeline = [{
  year: '2015',
  title: 'Establishment',
  description: "The African Union Commission expanded its global presence to deepen Africa's engagement with international partners."
}, {
  year: '2018',
  title: 'Expanded Mandate',
  description: 'Mandate expanded to include procurement coordination and project delivery across AU member states.'
}, {
  year: '2020',
  title: 'Strategic Framework Launch',
  description: 'Launched a five-year strategic framework focused on trade, health, education, and infrastructure.'
}, {
  year: '2023',
  title: 'AfCFTA Acceleration',
  description: 'Played a key role in supporting the African Continental Free Trade Area implementation worldwide.'
}, {
  year: '2025',
  title: 'Global Partnerships Era',
  description: 'Launched the Global Partnerships Initiative connecting Africa with worldwide investors and institutions.'
}, {
  year: '2026',
  title: 'Today',
  description: 'Coordinating £180M+ in active projects across 28 member states with 45+ ongoing initiatives.'
}];
export function About() {
  return <div data-id="element-431" data-mp="50a6t3ewqyob">
      <PageHero title="About Us" subtitle="The African Union Commission — bridging Africa and the world" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'About'
    }]} data-id="element-432" data-mp="4we1ygsznf0i" />

      <section className="py-16 bg-white" data-id="element-433" data-mp="sm8esydb53kr">
        <div className="max-w-7xl mx-auto px-4" data-id="element-434" data-mp="omzjvq6j6znm">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-435" data-mp="om6lbchq7ggk">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-436" data-mp="3dfbqk43wo6di">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-4" data-id="element-437" data-mp="r6bbgecvqkv8">
                Who We Are
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-438" data-mp="w5nyaz3r3rxt"></div>
              <p className="text-gray-700 text-lg leading-relaxed mb-4" data-id="element-439" data-mp="1juqee2iqlcq7">
                The African Union Commission is the Secretariat of the African
                Union, entrusted with executive functions across the continent.
                We serve as a strategic platform connecting Africa's 55 member
                states with global partners — including governments,
                multilateral institutions, the private sector, civil society,
                and the African diaspora.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-440" data-mp="3gh06187l4d3">
                We coordinate flagship projects, manage procurement processes,
                foster partnerships, and represent the African Union's interests
                on the international stage. Our work supports the realisation of
                Agenda 2063 — Africa's blueprint for inclusive and sustainable
                development.
              </p>
              <Link to="/projects" data-id="element-441" data-mp="wkawkz6vymx">
                <Button variant="primary" className="flex items-center gap-2" data-id="element-442" data-mp="39bcgqwgf2pms">
                  Explore Our Projects
                  <ArrowRightIcon size={18} data-id="element-443" data-mp="34danjipa19xl" />
                </Button>
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="grid grid-cols-2 gap-4" data-id="element-444" data-mp="3j2gszoepjwdx">
              <Card className="bg-au-green text-white border-au-green text-center py-8" data-id="element-445" data-mp="3b9x0ubl7tgae">
                <div className="text-4xl font-bold mb-2" data-id="element-446" data-mp="3fa7janq71z65">55</div>
                <div className="text-sm text-au-green-light" data-id="element-447" data-mp="3k27btv3xppsp">
                  AU Member States
                </div>
              </Card>
              <Card className="bg-white text-center py-8" data-id="element-448" data-mp="3p0sd7xuxafiq">
                <div className="text-4xl font-bold text-au-green mb-2" data-id="element-449" data-mp="2sc2uy6fdvkw8">45+</div>
                <div className="text-sm text-gray-600" data-id="element-450" data-mp="2wcmqx6o80igs">Active Projects</div>
              </Card>
              <Card className="bg-white text-center py-8" data-id="element-451" data-mp="31bzgw0ae25nl">
                <div className="text-4xl font-bold text-au-green mb-2" data-id="element-452" data-mp="31ao9zrlnchs9">
                  £180M
                </div>
                <div className="text-sm text-gray-600" data-id="element-453" data-mp="368zmka3bt6e6">Project Portfolio</div>
              </Card>
              <Card className="bg-au-green text-white border-au-green text-center py-8" data-id="element-454" data-mp="gnuxrg7qk8uc">
                <div className="text-4xl font-bold mb-2" data-id="element-455" data-mp="lj0i4alhpfgb">150+</div>
                <div className="text-sm text-au-green-light" data-id="element-456" data-mp="261ughc593fsc">
                  Partner Suppliers
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-ivory" data-id="element-457" data-mp="1wbrnhby4q1u0">
        <div className="max-w-7xl mx-auto px-4" data-id="element-458" data-mp="1rdzmztjf57e7">
          <div className="grid md:grid-cols-2 gap-8" data-id="element-459" data-mp="1rd81jbjjcq1n">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-460" data-mp="1ncebabimez5l">
              <Card className="h-full" data-id="element-461" data-mp="18h88ryiqhih0">
                <div className="w-14 h-14 bg-au-green rounded-lg flex items-center justify-center mb-4" data-id="element-462" data-mp="37srfs7kh8go1">
                  <TargetIcon size={28} className="text-white" data-id="element-463" data-mp="32swoi2wshp2g" />
                </div>
                <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-464" data-mp="2bnajnm4ml4nk">
                  Our Mission
                </h3>
                <div className="w-12 h-1 bg-au-gold mb-4" data-id="element-465" data-mp="2glu8pveimwk5"></div>
                <p className="text-gray-700 leading-relaxed" data-id="element-466" data-mp="22vh33zp3jtxz">
                  To advance the African Union's vision by fostering strategic
                  partnerships, coordinating impactful projects, and ensuring
                  transparent procurement that delivers tangible benefits to
                  African citizens and the global community.
                </p>
              </Card>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-467" data-mp="1jxehluvrcf4x">
              <Card className="h-full" data-id="element-468" data-mp="16o4a9rrd0xnu">
                <div className="w-14 h-14 bg-au-green rounded-lg flex items-center justify-center mb-4" data-id="element-469" data-mp="16p5mw0o0xs7o">
                  <GlobeIcon size={28} className="text-white" data-id="element-470" data-mp="12l7ltkf527bz" />
                </div>
                <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-471" data-mp="1gg076zhpmhqh">
                  Our Vision
                </h3>
                <div className="w-12 h-1 bg-au-gold mb-4" data-id="element-472" data-mp="1bm5onzduc8sz"></div>
                <p className="text-gray-700 leading-relaxed" data-id="element-473" data-mp="1qbk49p80fmgt">
                  An integrated, prosperous, and peaceful Africa — driven by its
                  own citizens and representing a dynamic force on the global
                  stage, as envisioned by Agenda 2063.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-id="element-474" data-mp="50l9fthoflub">
        <div className="max-w-7xl mx-auto px-4" data-id="element-475" data-mp="51mpkcdjuyiw">
          <SectionHeading title="Our Mandate" subtitle="Four strategic pillars guide our work in advancing Africa's global partnerships" centered data-id="element-476" data-mp="1igv8ivv838" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12" data-id="element-477" data-mp="37emyhbwumv9n">
            {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} data-id="element-478" data-mp="2yerj0uw9ge3t" data-mpi={index}>
                  <Card hover className="h-full text-center" data-id="element-479" data-mp="inc1ay0ue2zd">
                    <div className="w-16 h-16 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-480" data-mp="iowuokbf7fvn">
                      <Icon size={32} className="text-au-green" data-id="element-481" data-mp="l81mek7a8dsw" />
                    </div>
                    <h3 className="font-bold text-charcoal mb-2 text-lg" data-id="element-482" data-mp="q6d1pagcjgii">
                      {pillar.title}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-483" data-mp="v4wn99r12qac">
                      {pillar.description}
                    </p>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-ivory" data-id="element-484" data-mp="195h3pz9h0a0o">
        <div className="max-w-7xl mx-auto px-4" data-id="element-485" data-mp="19462a63hzecq">
          <SectionHeading title="Leadership Team" subtitle="Experienced leaders driving Africa's global engagement and project delivery" centered data-id="element-486" data-mp="1dw7angxjokxh" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12" data-id="element-487" data-mp="1ix3cloyc5x09">
            {leadership.map((leader, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} data-id="element-488" data-mp="1rvfdqkwtwd6y" data-mpi={index}>
                <Card hover className="text-center h-full" data-id="element-489" data-mp="3swda6q9i9hfo">
                  <div className="w-24 h-24 bg-au-green rounded-full flex items-center justify-center mx-auto mb-4 ring-4 ring-au-green-light" data-id="element-490" data-mp="bg52wwjfxynq">
                    <span className="text-white text-2xl font-serif" data-id="element-491" data-mp="kieabdjexbww">
                      {leader.initials}
                    </span>
                  </div>
                  <h3 className="font-bold text-charcoal mb-1" data-id="element-492" data-mp="fjterrf3vges">
                    {leader.name}
                  </h3>
                  <div className="text-sm font-medium text-au-green mb-3" data-id="element-493" data-mp="2mbkozqraik56">
                    {leader.role}
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-494" data-mp="ws41f32rvie4">{leader.bio}</p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-id="element-495" data-mp="o0agl4g7nliq">
        <div className="max-w-7xl mx-auto px-4" data-id="element-496" data-mp="nz96p3axl52l">
          <SectionHeading title="Organisational Structure" subtitle="A streamlined structure designed for effective delivery and accountability" centered data-id="element-497" data-mp="nyhgk803rzjp" />

          <div className="mt-12 max-w-4xl mx-auto" data-id="element-498" data-mp="j17kwam4ernf">
            <div className="flex flex-col items-center" data-id="element-499" data-mp="j20j2n70k8ty">
              <Card className="bg-au-green text-white border-au-green text-center w-full max-w-md mb-6" data-id="element-500" data-mp="51pszmdj2y9u">
                <BuildingIcon size={32} className="mx-auto mb-2" data-id="element-501" data-mp="92i12canyx4j" />
                <div className="text-sm text-au-green-light mb-1" data-id="element-502" data-mp="94utjje1spvu">
                  Office of the
                </div>
                <div className="font-bold text-lg" data-id="element-503" data-mp="23gsruqatk1cy">Regional Director</div>
              </Card>

              <div className="w-px h-8 bg-gray-border" data-id="element-504" data-mp="1eqes58tp87zh"></div>

              <div className="grid md:grid-cols-3 gap-4 w-full" data-id="element-505" data-mp="1epn54me63ikt">
                {['Programmes', 'Procurement', 'Partnerships'].map((dept, i) => <Card key={i} hover className="text-center" data-id="element-506" data-mp="14r7cug3em39q" data-mpi={i}>
                      <div className="w-12 h-12 bg-au-green-light rounded-lg flex items-center justify-center mx-auto mb-3" data-id="element-507" data-mp="zte7tqdu7fb1">
                        <UsersIcon size={24} className="text-au-green" data-id="element-508" data-mp="vrsl7dbi3mnn" />
                      </div>
                      <div className="font-bold text-charcoal mb-1" data-id="element-509" data-mp="r0kggul48pgt">
                        Department of
                      </div>
                      <div className="text-au-green font-serif text-xl" data-id="element-510" data-mp="m20oacfxihg0">
                        {dept}
                      </div>
                    </Card>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-ivory" data-id="element-511" data-mp="8314pj79wsqp">
        <div className="max-w-7xl mx-auto px-4" data-id="element-512" data-mp="84c65cd8xoen">
          <SectionHeading title="Our History" subtitle="A decade of advancing Africa's global partnerships" centered data-id="element-513" data-mp="2jo9zb7t63fa7" />

          <div className="mt-12 max-w-4xl mx-auto" data-id="element-514" data-mp="2sq9gc046mjd5">
            <div className="relative" data-id="element-515" data-mp="2sqj6bnp88qiu">
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-au-green-light md:-translate-x-1/2" data-id="element-516" data-mp="2xp2rvmzws0ao"></div>

              <div className="space-y-8" data-id="element-517" data-mp="2023tn7tkikao">
                {timeline.map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.05
              }} className={`relative flex items-start md:items-center gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-518" data-mp="24zvy11befyvr" data-mpi={index}>
                    <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-au-gold rounded-full -translate-x-1/2 ring-4 ring-ivory z-10 mt-2 md:mt-0" data-id="element-519" data-mp="1nyrptkmqfdso"></div>

                    <div className={`pl-20 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`} data-id="element-520" data-mp="1j0q8b44yfmjb">
                      <Card hover data-id="element-521" data-mp="r9k0s2mwmmk0">
                        <div className="text-2xl font-serif text-au-green mb-1" data-id="element-522" data-mp="ralfch34pr6h">
                          {item.year}
                        </div>
                        <h3 className="font-bold text-charcoal mb-2" data-id="element-523" data-mp="108xex8lz3zb2">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-600" data-id="element-524" data-mp="2gm5hb37nvh5e">
                          {item.description}
                        </p>
                      </Card>
                    </div>

                    <div className="hidden md:block md:w-1/2" data-id="element-525" data-mp="3tlh41773mcqs"></div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-au-green text-white" data-id="element-526" data-mp="3kmvnfhl6t7sn">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-527" data-mp="3pkdnsjl52ep1">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-528" data-mp="3pkx4xy2ysz20">
            <h2 className="text-3xl md:text-4xl font-serif mb-4" data-id="element-529" data-mp="cdvcogjtzq4m">
              Partner With Us
            </h2>
            <div className="w-24 h-1 bg-au-gold mx-auto mb-6" data-id="element-530" data-mp="103fz59f715in"></div>
            <p className="text-xl text-au-green-light mb-8" data-id="element-531" data-mp="14ufvodkehlkm">
              Whether you're a supplier, investor, or institution, there are
              many ways to engage with the African Union Commission.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-532" data-mp="2w6cbcqipk4eb">
              <Link to="/procurement/supplier-registration" data-id="element-533" data-mp="2w519iw8tp6pu">
                <Button variant="secondary" className="bg-white" data-id="element-534" data-mp="2r8j8o7gm5sis">
                  Submit Expression of Interest
                </Button>
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-medium border-2 border-white text-white bg-transparent hover:bg-white hover:text-au-green transition-colors duration-300" data-id="element-535" data-mp="3jw6nf5v7doxi">
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}